import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowDown01,
  ArrowUp,
  ChevronDown,
  Copy,
  Eraser,
  Filter,
  GripVertical,
  Keyboard,
  Plus,
  Redo2,
  Search,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { GameBankRules } from "@/Api/gameBank";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  appDialogPrimaryButton,
} from "@/components/ui/app-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { pad2, STATUS_STYLE } from "../shared";
import {
  cellLength,
  CellPos,
  CellRange,
  clamp,
  clearRange,
  deleteRows,
  duplicateRows,
  findMatches,
  freshStatus,
  insertRows,
  isFilled,
  isIncomplete,
  matchesFilter,
  MAX_ROWS,
  moveColumnsBy,
  moveRowsBy,
  moveRowsTo,
  normalizeRange,
  parseClipboard,
  pasteMatrix,
  rangeSize,
  removeDuplicateRows,
  removeEmptyRows,
  RowResult,
  rowProblems,
  rowSignature,
  setCell,
  settle,
  SheetFilter,
  SheetRow,
  sortRowNumbers,
  toTsv,
} from "./model";

/** Linhas que PageUp/PageDown pulam */
const PAGE_ROWS = 15;
const BLUE = "#2563eb";

const FILTER_LABELS: Record<SheetFilter, string> = {
  all: "Todas",
  problems: "Com problema",
  repeated: "Repetidos",
  attention: "Atenção",
  invalid: "Inválidas",
  incomplete: "Incompletas",
};

const FILTERS: SheetFilter[] = ["all", "problems", "repeated", "attention", "invalid", "incomplete"];

const SHORTCUTS: { title: string; items: [string, string][] }[] = [
  {
    title: "Navegar",
    items: [
      ["Mover uma célula", "Setas"],
      ["Descer / subir", "Enter / Shift+Enter"],
      ["Direita / esquerda", "Tab / Shift+Tab"],
      ["Primeira / última linha ou coluna", "Ctrl+setas"],
      ["Início / fim da linha", "Home / End"],
      ["Primeira / última célula", "Ctrl+Home / Ctrl+End"],
    ],
  },
  {
    title: "Selecionar",
    items: [
      ["Aumentar a seleção", "Shift+setas"],
      ["Selecionar uma área", "Clicar e arrastar"],
      ["Selecionar até a célula", "Shift+clique"],
      ["Linha inteira", "Shift+Espaço"],
      ["Coluna inteira", "Ctrl+Espaço"],
      ["Planilha toda", "Ctrl+A"],
    ],
  },
  {
    title: "Editar",
    items: [
      ["Substituir a célula", "Digitar"],
      ["Editar sem apagar", "F2"],
      ["Limpar a seleção", "Delete"],
      ["Cancelar a edição", "Esc"],
      ["Desfazer", "Ctrl+Z"],
      ["Refazer", "Ctrl+Y"],
    ],
  },
  {
    title: "Copiar e colar",
    items: [
      ["Copiar (cola no Excel)", "Ctrl+C"],
      ["Recortar", "Ctrl+X"],
      ["Colar do Excel ou Google Planilhas", "Ctrl+V"],
      ["Um valor colado numa área preenche a área", "Ctrl+V"],
    ],
  },
  {
    title: "Linhas e colunas",
    items: [
      ["Inserir linha abaixo", "Ctrl+Shift+="],
      ["Excluir linhas", "Ctrl+-"],
      ["Duplicar linhas", "Ctrl+Shift+D"],
      ["Mover linhas", "Alt+↑ / Alt+↓"],
      ["Mover colunas", "Alt+← / Alt+→"],
    ],
  },
  {
    title: "Localizar",
    items: [
      ["Localizar dezena", "Ctrl+F"],
      ["Próxima / anterior", "F3 / Shift+F3"],
      ["Próximo problema", "F8"],
      ["Esta lista", "?"],
    ],
  },
];

type DragMode = "select" | "rows" | "cols" | "move";

type MenuItem = { label: string; shortcut?: string; action: () => void; danger?: boolean; hidden?: boolean } | "sep";

interface MenuState {
  kind: "cells" | "column";
  x: number;
  y: number;
}

interface GridRowProps {
  row: SheetRow;
  /** Posição na tela (com filtro, é diferente da posição na planilha) */
  r: number;
  /** Linha na planilha (1 a N) */
  number: number;
  rules: GameBankRules;
  zebra: boolean;
  /** Colunas selecionadas nesta linha (-1 quando a linha está fora da seleção) */
  selC1: number;
  selC2: number;
  selTop: boolean;
  selBottom: boolean;
  /** Coluna da célula ativa nesta linha (-1 quando não é a linha ativa) */
  activeCol: number;
  fullRow: boolean;
  /** Colunas com a dezena procurada, separadas por vírgula */
  findCols: string;
  currentFindCol: number;
  result?: RowResult;
  matchLabel: string;
  dimmed: boolean;
  dropTop: boolean;
  dropBottom: boolean;
  readOnly: boolean;
}

const MatchCell = ({
  cells,
  result,
  matchLabel,
}: {
  cells: string[];
  result?: RowResult;
  matchLabel: string;
}) => {
  if (!isFilled(cells)) {
    return <div className="rounded-md bg-slate-100 px-2 py-1.5 text-xs text-slate-500 dark:bg-slate-800">Aguardando</div>;
  }

  const stale = !!result && result.signature !== rowSignature(cells);

  if (!result || stale) {
    return (
      <div className="rounded-md bg-slate-100 px-2 py-1.5 text-xs text-slate-500 dark:bg-slate-800">
        {result ? "Reconferir" : "Não conferido"}
      </div>
    );
  }

  const { res } = result;
  const style = STATUS_STYLE[res.status];

  if (res.status === "invalid") {
    return <div className={cn("rounded-md border px-2 py-1.5 text-xs", style.box)}>{res.errors[0] ?? "Linha inválida"}</div>;
  }

  if (!res.match) {
    return <div className={cn("rounded-md border px-2 py-1.5 text-xs font-semibold", style.box)}>Sem coincidência</div>;
  }

  const origin =
    res.match.source === "bank"
      ? `Jogo Nº ${String(res.match.game_number).padStart(3, "0")} · ${res.match.dezenas} dez.`
      : matchLabel;

  return (
    <div className={cn("rounded-md border px-2 py-1.5 text-xs leading-tight", style.box)}>
      <p className="font-semibold">
        {res.equal_count} números iguais de {cells.length}
      </p>
      <p className="opacity-90">
        {origin}
        {res.status === "repeated" &&
          (res.match.dezenas > cells.length ? ` · Contido num jogo de ${res.match.dezenas}` : " · Repetido")}
      </p>
    </div>
  );
};

const GridRow = memo(function GridRow({
  row,
  r,
  number,
  rules,
  zebra,
  selC1,
  selC2,
  selTop,
  selBottom,
  activeCol,
  fullRow,
  findCols,
  currentFindCol,
  result,
  matchLabel,
  dimmed,
  dropTop,
  dropBottom,
  readOnly,
}: GridRowProps) {
  const problems = useMemo(() => rowProblems(row.cells, rules), [row.cells, rules]);
  const found = useMemo(() => new Set(findCols ? findCols.split(",").map(Number) : []), [findCols]);
  const drop = dropTop ? `inset 0 3px 0 ${BLUE}` : dropBottom ? `inset 0 -3px 0 ${BLUE}` : "";
  const inRows = selC1 >= 0;

  return (
    <tr aria-rowindex={r + 2} className={cn(zebra && "bg-violet-50/60 dark:bg-violet-950/20", dimmed && "opacity-40")}>
      <th
        scope="row"
        data-row-header={r}
        className={cn(
          "sticky left-0 z-10 h-10 w-14 min-w-14 select-none border-b border-r px-1 text-center text-xs font-semibold",
          fullRow
            ? "bg-blue-600 text-white"
            : inRows
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200"
              : "bg-white text-muted-foreground dark:bg-zinc-950",
          !readOnly && "cursor-pointer"
        )}
        style={drop ? { boxShadow: drop } : undefined}
      >
        <span className="inline-flex items-center gap-0.5">
          {!readOnly && (
            <span data-grip={r} title="Arraste para mover a linha" className="cursor-grab rounded p-0.5 opacity-60 hover:opacity-100">
              <GripVertical className="h-3 w-3" />
            </span>
          )}
          {number}
        </span>
      </th>
      {row.cells.map((cell, c) => {
        const inSel = inRows && c >= selC1 && c <= selC2;
        const active = c === activeCol;
        const shadows: string[] = [];
        if (inSel) {
          if (selTop) shadows.push(`inset 0 2px 0 ${BLUE}`);
          if (selBottom) shadows.push(`inset 0 -2px 0 ${BLUE}`);
          if (c === selC1) shadows.push(`inset 2px 0 0 ${BLUE}`);
          if (c === selC2) shadows.push(`inset -2px 0 0 ${BLUE}`);
        }
        if (active) shadows.push(`inset 0 0 0 2px ${BLUE}`);
        if (drop) shadows.push(drop);

        return (
          <td
            key={c}
            data-r={r}
            data-c={c}
            aria-colindex={c + 2}
            aria-selected={inSel || active}
            className={cn(
              "h-10 w-9 min-w-9 select-none scroll-ml-16 scroll-mt-10 border-b border-r border-slate-200/70 text-center text-sm font-medium dark:border-slate-700/70",
              inSel && !active && "bg-blue-100 dark:bg-blue-900/40",
              active && "bg-white dark:bg-zinc-900",
              found.has(c) && "bg-amber-200 font-bold dark:bg-amber-700/50",
              problems[c] && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
            )}
            style={
              shadows.length || c === currentFindCol
                ? { boxShadow: [...shadows, ...(c === currentFindCol ? ["inset 0 0 0 2px #d97706"] : [])].join(",") }
                : undefined
            }
          >
            {cell}
          </td>
        );
      })}
      <td className="min-w-52 border-b px-2 py-1.5" style={drop ? { boxShadow: drop } : undefined}>
        <MatchCell cells={row.cells} result={result} matchLabel={matchLabel} />
      </td>
    </tr>
  );
});

export interface SheetGridProps {
  rows: SheetRow[];
  columns: number;
  rules: GameBankRules;
  /** Finalizada, arquivada ou sem permissão: seleciona, copia, localiza e filtra, mas não edita */
  readOnly: boolean;
  /** Resultado da conferência por id da linha */
  results: Record<string, RowResult>;
  canUndo: boolean;
  canRedo: boolean;
  undoSteps: number;
  /** Toda alteração das células (a página guarda no histórico e agenda o autosave) */
  onCommit: (rows: SheetRow[]) => void;
  onUndo: () => void;
  onRedo: () => void;
  /** Coloca o foco na planilha ao abrir (planilha nova) */
  autoFocus?: boolean;
}

/**
 * Banco de Jogos (LOT-62): a planilha com recursos do Excel. Células são texto; um único campo de edição
 * fica sobre a célula ativa e recebe o teclado, o copiar e o colar. A seleção é um retângulo (âncora e
 * foco) em posição na tela; com filtro, as posições são ligadas às linhas reais por `visible`.
 */
export const SheetGrid = ({
  rows,
  columns,
  rules,
  readOnly,
  results,
  canUndo,
  canRedo,
  undoSteps,
  onCommit,
  onUndo,
  onRedo,
  autoFocus,
}: SheetGridProps) => {
  const [anchor, setAnchor] = useState<CellPos>({ r: 0, c: 0 });
  const [focus, setFocus] = useState<CellPos>({ r: 0, c: 0 });
  const [edit, setEdit] = useState<{ value: string } | null>(null);
  const [editorBox, setEditorBox] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [filter, setFilter] = useState<SheetFilter>("all");
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState("");
  const [findIndex, setFindIndex] = useState(-1);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [moveTarget, setMoveTarget] = useState<number | null>(null);
  const [moving, setMoving] = useState<{ r1: number; r2: number } | null>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [notice, setNotice] = useState("");

  const editorRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const findRef = useRef<HTMLInputElement>(null);
  const dragRef = useRef<{ mode: DragMode; r1: number; r2: number } | null>(null);
  const scrollToFocus = useRef(false);
  const latest = useRef<{ mouseUp: () => void }>({ mouseUp: () => undefined });

  const structuralLocked = filter !== "all";
  const ordered = rules.ordered;
  const colLabel = (c: number) => (ordered ? `${c + 1}º` : pad2(c + 1));

  const visible = useMemo(
    () =>
      rows
        .map((_, index) => index)
        .filter((index) => matchesFilter(rows[index].cells, filter, rules, freshStatus(rows[index], results))),
    [rows, filter, rules, results]
  );

  const lastRow = Math.max(visible.length - 1, 0);
  const range = useMemo(() => normalizeRange(anchor, focus), [anchor, focus]);
  const selectedIndexes = useMemo(
    () => visible.slice(range.r1, range.r2 + 1),
    [visible, range.r1, range.r2]
  );
  const fullRows = range.c1 === 0 && range.c2 === columns - 1;
  const fullColumns = range.r1 === 0 && range.r2 === lastRow;
  const activeIndex = visible[anchor.r];
  const activeValue = activeIndex !== undefined ? rows[activeIndex]?.cells[anchor.c] ?? "" : "";

  const filterCounts = useMemo(() => {
    const counts = {} as Record<SheetFilter, number>;
    FILTERS.forEach((option) => {
      counts[option] =
        option === "all"
          ? rows.length
          : rows.filter((row) => matchesFilter(row.cells, option, rules, freshStatus(row, results))).length;
    });
    return counts;
  }, [rows, rules, results]);

  const matches = useMemo(
    () => (findOpen ? findMatches(rows, visible, findQuery) : []),
    [findOpen, rows, visible, findQuery]
  );

  const findByRow = useMemo(() => {
    const byRow = new Map<number, number[]>();
    matches.forEach(({ r, c }) => byRow.set(r, [...(byRow.get(r) ?? []), c]));
    return byRow;
  }, [matches]);

  const currentMatch = findIndex >= 0 ? matches[findIndex] : undefined;

  const rowPosition = useMemo(() => {
    const positions = new Map<string, number>();
    rows.forEach((row, index) => positions.set(row.id, index));
    return positions;
  }, [rows]);

  const filled = useMemo(() => rows.filter((row) => isFilled(row.cells)).length, [rows]);
  const incomplete = useMemo(() => rows.filter((row) => isIncomplete(row.cells)).length, [rows]);

  // ------------------------------------------------------------------ foco, seleção e posição do editor

  const focusGrid = () => editorRef.current?.focus({ preventScroll: true });

  /**
   * Seleciona de `nextAnchor` (célula ativa) até `nextFocus`. `limitRows`: prende na última linha visível;
   * depois de criar linhas (inserir, colar, digitar no fim) vai sem limite, porque a lista ainda não cresceu.
   */
  const select = (nextAnchor: CellPos, nextFocus: CellPos = nextAnchor, scroll = true, limitRows = true) => {
    const fix = (pos: CellPos) => ({
      r: limitRows ? clamp(pos.r, 0, lastRow) : Math.max(pos.r, 0),
      c: clamp(pos.c, 0, columns - 1),
    });
    setAnchor(fix(nextAnchor));
    setFocus(fix(nextFocus));
    scrollToFocus.current = scroll;
  };

  useEffect(() => {
    if (autoFocus) editorRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  // o aviso da barra de status ("Copiado: ...") some sozinho
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(""), 4000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  // a planilha encolheu (excluir, filtrar): a seleção volta para dentro
  useEffect(() => {
    const max = Math.max(visible.length - 1, 0);
    if (anchor.r > max || focus.r > max || anchor.c >= columns || focus.c >= columns) {
      setAnchor((pos) => ({ r: Math.min(pos.r, max), c: Math.min(pos.c, columns - 1) }));
      setFocus((pos) => ({ r: Math.min(pos.r, max), c: Math.min(pos.c, columns - 1) }));
    }
  }, [visible.length, columns, anchor, focus]);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const cell = wrapper?.querySelector<HTMLElement>(`td[data-r="${anchor.r}"][data-c="${anchor.c}"]`);
    if (!wrapper || !cell) return;
    const outer = wrapper.getBoundingClientRect();
    const box = cell.getBoundingClientRect();
    setEditorBox({ top: box.top - outer.top, left: box.left - outer.left, width: box.width, height: box.height });
  }, [anchor, rows, visible, columns, edit]);

  useEffect(() => {
    if (!scrollToFocus.current) return;
    scrollToFocus.current = false;
    wrapperRef.current
      ?.querySelector<HTMLElement>(`td[data-r="${focus.r}"][data-c="${focus.c}"]`)
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [focus]);

  // ------------------------------------------------------------------ edição

  const commit = (next: SheetRow[]) => {
    if (next !== rows) onCommit(next);
  };

  const ensureEditable = (structural = false) => {
    if (readOnly) return false;
    if (structural && structuralLocked) {
      toast.warning("Mostre todas as linhas para inserir, mover ou excluir linhas.");
      return false;
    }
    return true;
  };

  /** Grava a célula ativa. `pad`: "5" vira "05" (saindo da célula) */
  const commitEdit = (value = edit?.value ?? "", pad = true) => {
    setEdit(null);
    if (activeIndex === undefined || readOnly) return;
    const final = pad && value.length === 1 && cellLength(rules) === 2 ? value.padStart(2, "0") : value;
    commit(setCell(rows, activeIndex, anchor.c, final));
  };

  /** Depois de completar uma dezena: próxima coluna; no fim da linha, próxima linha (cria uma no fim) */
  const advanceAfterTyping = (next: SheetRow[]) => {
    if (anchor.c < columns - 1) {
      select({ r: anchor.r, c: anchor.c + 1 });
      return next;
    }

    if (anchor.r < lastRow) {
      select({ r: anchor.r + 1, c: 0 });
      return next;
    }

    if (!structuralLocked && next.length < MAX_ROWS) {
      const grown = insertRows(next, next.length, 1, columns).rows;
      select({ r: anchor.r + 1, c: 0 }, { r: anchor.r + 1, c: 0 }, true, false);
      return grown;
    }

    return next;
  };

  const handleEditorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly || activeIndex === undefined) return;
    const digits = event.target.value.replace(/\D/g, "").slice(0, cellLength(rules));

    if (!edit && digits === "") return;

    const { value, done } = settle(digits, rules);

    if (done) {
      setEdit(null);
      commit(advanceAfterTyping(setCell(rows, activeIndex, anchor.c, value)));
      return;
    }

    setEdit({ value });
  };

  const handleEditorBlur = () => {
    if (edit) commitEdit();
  };

  // ------------------------------------------------------------------ copiar, recortar, colar

  const writeClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const area = document.createElement("textarea");
      area.value = text;
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      area.remove();
      focusGrid();
    }
  };

  const describe = (target: CellRange) => {
    const size = rangeSize(target);
    return `${size.rows} ${size.rows === 1 ? "linha" : "linhas"} × ${size.cols} ${ordered ? (size.cols === 1 ? "coluna" : "colunas") : size.cols === 1 ? "dezena" : "dezenas"}`;
  };

  const copyRange = (target: CellRange = range) => {
    const indexes = visible.slice(target.r1, target.r2 + 1);
    void writeClipboard(toTsv(rows, indexes, target.c1, target.c2));
    setNotice(`Copiado: ${describe(target)}`);
  };

  const cutRange = (target: CellRange = range) => {
    copyRange(target);
    if (!ensureEditable()) return;
    commit(clearRange(rows, visible, target));
    setNotice(`Recortado: ${describe(target)}`);
  };

  const pasteText = (text: string, target: CellRange = range, asNewRows = false) => {
    if (!ensureEditable(asNewRows)) return;
    const matrix = parseClipboard(text, rules);
    if (!matrix.length) return;

    let base = rows;
    let at = target;

    if (asNewRows) {
      const insertedAt = target.r2 + 1;
      const inserted = insertRows(rows, insertedAt, matrix.length, columns);
      if (!inserted.inserted) {
        toast.warning(`Máximo de ${MAX_ROWS} jogos por planilha.`);
        return;
      }
      base = inserted.rows;
      at = { r1: insertedAt, r2: insertedAt, c1: 0, c2: 0 };
    }

    const map = asNewRows ? base.map((_, index) => index) : visible;
    const result = pasteMatrix(base, map, at, matrix, columns, !structuralLocked);
    commit(result.rows);
    select({ r: result.range.r1, c: result.range.c1 }, { r: result.range.r2, c: result.range.c2 }, true, false);
    setNotice(`Colado: ${describe(result.range)}`);

    if (result.trimmed) toast.warning(`Havia mais de ${columns} números em alguma linha; o excedente foi ignorado.`);
    if (result.capped) {
      toast.warning(
        structuralLocked
          ? "Com filtro, o colar para na última linha visível."
          : `Máximo de ${MAX_ROWS} jogos por planilha; o excedente foi ignorado.`
      );
    }
  };

  const pasteFromClipboard = (target: CellRange = range, asNewRows = false) => {
    navigator.clipboard
      .readText()
      .then((text) => pasteText(text, target, asNewRows))
      .catch(() => toast.info("Para colar, use Ctrl+V (⌘+V no Mac) com a planilha selecionada."));
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text/plain");
    // editando, um número só segue o comportamento normal do campo
    if (edit && !/[\t\n\r;, ]/.test(text.trim())) return;
    event.preventDefault();
    if (edit) setEdit(null);
    pasteText(text);
  };

  // ------------------------------------------------------------------ ações

  const clearSelection = (target: CellRange = range) => {
    if (!ensureEditable()) return;
    commit(clearRange(rows, visible, target));
  };

  const insert = (where: "above" | "below") => {
    if (!ensureEditable(true)) return;
    const count = range.r2 - range.r1 + 1;
    const at = where === "above" ? range.r1 : range.r2 + 1;
    const result = insertRows(rows, at, count, columns);
    if (!result.inserted) {
      toast.warning(`Máximo de ${MAX_ROWS} jogos por planilha.`);
      return;
    }
    commit(result.rows);
    select({ r: at, c: 0 }, { r: at + result.inserted - 1, c: columns - 1 }, true, false);
  };

  const duplicate = () => {
    if (!ensureEditable(true)) return;
    const result = duplicateRows(rows, selectedIndexes);
    if (!result.inserted) {
      toast.warning(`Máximo de ${MAX_ROWS} jogos por planilha.`);
      return;
    }
    commit(result.rows);
    select({ r: result.at, c: 0 }, { r: result.at + result.inserted - 1, c: columns - 1 }, true, false);
  };

  const removeRows = () => {
    if (!ensureEditable(true)) return;
    commit(deleteRows(rows, selectedIndexes, columns));
    select({ r: range.r1, c: anchor.c });
  };

  const moveRows = (delta: -1 | 1) => {
    if (!ensureEditable(true)) return;
    const next = moveRowsBy(rows, range.r1, range.r2, delta);
    if (!next) return;
    commit(next);
    select({ r: anchor.r + delta, c: anchor.c }, { r: focus.r + delta, c: focus.c });
  };

  const moveColumns = (delta: -1 | 1) => {
    if (!ensureEditable()) return;
    const next = moveColumnsBy(rows, range.c1, range.c2, delta, columns);
    if (!next) return;
    commit(next);
    select({ r: anchor.r, c: anchor.c + delta }, { r: focus.r, c: focus.c + delta });
  };

  const sortNumbers = () => {
    if (!ensureEditable() || ordered) return;
    commit(sortRowNumbers(rows, selectedIndexes));
  };

  const removeEmpty = () => {
    if (!ensureEditable(true)) return;
    const result = removeEmptyRows(rows, columns);
    if (!result.removed) {
      toast.info("Não há linhas vazias.");
      return;
    }
    commit(result.rows);
    select({ r: 0, c: 0 });
    toast.success(`${result.removed} ${result.removed === 1 ? "linha vazia removida" : "linhas vazias removidas"}.`);
  };

  const removeDuplicates = () => {
    if (!ensureEditable(true)) return;
    const result = removeDuplicateRows(rows, ordered);
    if (!result.removed) {
      toast.info("Não há jogos repetidos dentro da planilha.");
      return;
    }
    commit(result.rows);
    select({ r: 0, c: 0 });
    toast.success(`${result.removed} ${result.removed === 1 ? "jogo repetido removido" : "jogos repetidos removidos"}.`);
  };

  const selectAll = () => select({ r: 0, c: 0 }, { r: lastRow, c: columns - 1 }, false);
  const selectRows = () => select({ r: range.r1, c: 0 }, { r: range.r2, c: columns - 1 }, false);
  const selectColumns = () => select({ r: 0, c: range.c1 }, { r: lastRow, c: range.c2 }, false);

  const changeFilter = (next: SheetFilter) => {
    setFilter(next);
    setAnchor({ r: 0, c: 0 });
    setFocus({ r: 0, c: 0 });
    setFindIndex(-1);
  };

  // ------------------------------------------------------------------ localizar e próximo problema

  const openFind = () => {
    setFindOpen(true);
    setTimeout(() => findRef.current?.select(), 0);
  };

  const closeFind = () => {
    setFindOpen(false);
    setFindIndex(-1);
    focusGrid();
  };

  const stepFind = (direction: 1 | -1) => {
    if (!matches.length) {
      if (findOpen && findQuery) toast.info("Dezena não encontrada nas linhas visíveis.");
      return;
    }
    const index = (findIndex + direction + matches.length) % matches.length;
    setFindIndex(index);
    select(matches[index]);
  };

  const nextProblem = () => {
    for (let step = 1; step <= visible.length; step++) {
      const r = (anchor.r + step) % visible.length;
      const row = rows[visible[r]];
      if (matchesFilter(row.cells, "problems", rules, freshStatus(row, results))) {
        select({ r, c: 0 });
        focusGrid();
        return;
      }
    }
    toast.success("Nenhum problema nas linhas visíveis.");
  };

  // ------------------------------------------------------------------ teclado

  const arrow = (key: string, extend: boolean, jump: boolean) => {
    const from = extend ? focus : anchor;
    let { r, c } = from;
    if (key === "ArrowUp") r = jump ? 0 : r - 1;
    if (key === "ArrowDown") r = jump ? lastRow : r + 1;
    if (key === "ArrowLeft") c = jump ? 0 : c - 1;
    if (key === "ArrowRight") c = jump ? columns - 1 : c + 1;
    if (extend) select(anchor, { r, c });
    else select({ r, c });
  };

  const moveBy = (dr: number, dc: number) => select({ r: anchor.r + dr, c: anchor.c + dc });

  const goTo = (r: number, c: number, extend: boolean) => {
    if (extend) select(anchor, { r, c });
    else select({ r, c });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = event;
    const mod = event.ctrlKey || event.metaKey;

    if (edit) {
      if (key === "Escape") {
        event.preventDefault();
        setEdit(null);
      } else if (key === "Enter") {
        event.preventDefault();
        commitEdit();
        moveBy(event.shiftKey ? -1 : 1, 0);
      } else if (key === "Tab") {
        event.preventDefault();
        commitEdit();
        moveBy(0, event.shiftKey ? -1 : 1);
      } else if (key.startsWith("Arrow")) {
        event.preventDefault();
        commitEdit();
        arrow(key, false, false);
      } else if (key === "Backspace" && edit.value === "") {
        event.preventDefault();
        setEdit(null);
        moveBy(0, -1);
      }
      return;
    }

    if (mod && !event.altKey) {
      const lower = key.toLowerCase();
      if (lower === "v") return;
      event.preventDefault();
      if (lower === "z" && !event.shiftKey) onUndo();
      else if (lower === "y" || (lower === "z" && event.shiftKey)) onRedo();
      else if (lower === "c") copyRange();
      else if (lower === "x") cutRange();
      else if (lower === "a") selectAll();
      else if (lower === "f") openFind();
      else if (key === " ") selectColumns();
      else if (event.shiftKey && lower === "d") duplicate();
      else if (event.shiftKey && (key === "+" || key === "=")) insert("below");
      else if (key === "-") removeRows();
      else if (key === "Home") goTo(0, 0, event.shiftKey);
      else if (key === "End") goTo(lastRow, columns - 1, event.shiftKey);
      else if (key.startsWith("Arrow")) arrow(key, event.shiftKey, true);
      return;
    }

    if (event.altKey && !mod) {
      if (key === "ArrowUp" || key === "ArrowDown") {
        event.preventDefault();
        moveRows(key === "ArrowUp" ? -1 : 1);
      } else if (key === "ArrowLeft" || key === "ArrowRight") {
        event.preventDefault();
        moveColumns(key === "ArrowLeft" ? -1 : 1);
      }
      return;
    }

    switch (key) {
      case "ArrowUp":
      case "ArrowDown":
      case "ArrowLeft":
      case "ArrowRight":
        event.preventDefault();
        arrow(key, event.shiftKey, false);
        break;
      case "Enter":
        event.preventDefault();
        moveBy(event.shiftKey ? -1 : 1, 0);
        break;
      case "Tab":
        event.preventDefault();
        moveBy(0, event.shiftKey ? -1 : 1);
        break;
      case "Home":
        event.preventDefault();
        goTo(focus.r, 0, event.shiftKey);
        break;
      case "End":
        event.preventDefault();
        goTo(focus.r, columns - 1, event.shiftKey);
        break;
      case "PageUp":
        event.preventDefault();
        goTo(focus.r - PAGE_ROWS, focus.c, event.shiftKey);
        break;
      case "PageDown":
        event.preventDefault();
        goTo(focus.r + PAGE_ROWS, focus.c, event.shiftKey);
        break;
      case "Delete":
        event.preventDefault();
        clearSelection();
        break;
      case "Backspace":
        event.preventDefault();
        if (rangeSize(range).cells === 1 && activeValue === "") moveBy(0, -1);
        else clearSelection();
        break;
      case "F2":
        event.preventDefault();
        if (!readOnly) setEdit({ value: activeValue });
        break;
      case "F3":
        event.preventDefault();
        if (!findOpen) openFind();
        else stepFind(event.shiftKey ? -1 : 1);
        break;
      case "F8":
        event.preventDefault();
        nextProblem();
        break;
      case "Escape":
        event.preventDefault();
        if (menu) setMenu(null);
        else if (findOpen) closeFind();
        else select(anchor, anchor, false);
        break;
      case "?":
        event.preventDefault();
        setShortcutsOpen(true);
        break;
      case " ":
        if (event.shiftKey) {
          event.preventDefault();
          selectRows();
        }
        break;
      default:
    }
  };

  // ------------------------------------------------------------------ mouse

  const cellFrom = (target: EventTarget | null) => {
    const element = (target as HTMLElement | null)?.closest?.("[data-c],[data-row-header],[data-col-header]");
    if (!element) return null;
    const el = element as HTMLElement;
    if (el.dataset.c !== undefined) return { kind: "cell" as const, r: Number(el.dataset.r), c: Number(el.dataset.c), el };
    if (el.dataset.rowHeader !== undefined) return { kind: "row" as const, r: Number(el.dataset.rowHeader), c: 0, el };
    return { kind: "col" as const, r: 0, c: Number(el.dataset.colHeader), el };
  };

  const inRange = (r: number, c: number) => r >= range.r1 && r <= range.r2 && c >= range.c1 && c <= range.c2;

  const handleMouseDown = (event: React.MouseEvent<HTMLTableElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest("[data-col-menu]")) return;
    const hit = cellFrom(target);
    if (!hit) return;

    event.preventDefault();
    if (edit) commitEdit();
    setMenu(null);

    const rightClick = event.button === 2;

    if (target.closest("[data-grip]") && !readOnly) {
      const rowsSelected = fullRows && hit.r >= range.r1 && hit.r <= range.r2;
      const block = rowsSelected ? { r1: range.r1, r2: range.r2 } : { r1: hit.r, r2: hit.r };
      if (!rowsSelected) select({ r: hit.r, c: 0 }, { r: hit.r, c: columns - 1 }, false);
      dragRef.current = { mode: "move", ...block };
      setMoving(block);
      setMoveTarget(null);
      focusGrid();
      return;
    }

    if (hit.kind === "row") {
      if (!(rightClick && fullRows && hit.r >= range.r1 && hit.r <= range.r2)) {
        if (event.shiftKey) select({ r: anchor.r, c: 0 }, { r: hit.r, c: columns - 1 }, false);
        else select({ r: hit.r, c: 0 }, { r: hit.r, c: columns - 1 }, false);
      }
      dragRef.current = rightClick ? null : { mode: "rows", r1: hit.r, r2: hit.r };
    } else if (hit.kind === "col") {
      if (!(rightClick && fullColumns && hit.c >= range.c1 && hit.c <= range.c2)) {
        if (event.shiftKey) select({ r: 0, c: anchor.c }, { r: lastRow, c: hit.c }, false);
        else select({ r: 0, c: hit.c }, { r: lastRow, c: hit.c }, false);
      }
      dragRef.current = rightClick ? null : { mode: "cols", r1: 0, r2: 0 };
    } else {
      if (!(rightClick && inRange(hit.r, hit.c))) {
        if (event.shiftKey) select(anchor, { r: hit.r, c: hit.c }, false);
        else select({ r: hit.r, c: hit.c }, { r: hit.r, c: hit.c }, false);
      }
      dragRef.current = rightClick ? null : { mode: "select", r1: hit.r, r2: hit.r };
    }

    focusGrid();
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLTableElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const hit = cellFrom(event.target);
    if (!hit) return;

    if (drag.mode === "move") {
      if (hit.kind === "col") return;
      const box = hit.el.getBoundingClientRect();
      setMoveTarget(event.clientY > box.top + box.height / 2 ? hit.r + 1 : hit.r);
      return;
    }

    if (drag.mode === "select" && hit.kind === "cell") setFocus({ r: hit.r, c: hit.c });
    if (drag.mode === "rows" && hit.kind !== "col") setFocus({ r: hit.r, c: columns - 1 });
    if (drag.mode === "cols" && hit.kind !== "row") setFocus({ r: lastRow, c: hit.c });
  };

  const handleMouseUp = () => {
    const drag = dragRef.current;
    dragRef.current = null;

    if (drag?.mode === "move") {
      setMoving(null);
      setMoveTarget(null);
      const target = moveTarget;
      if (target === null || target === undefined || (target >= drag.r1 && target <= drag.r2 + 1)) return;
      if (!ensureEditable(true)) return;
      const result = moveRowsTo(rows, drag.r1, drag.r2, target);
      commit(result.rows);
      select({ r: result.start, c: 0 }, { r: result.start + drag.r2 - drag.r1, c: columns - 1 }, false);
    }
  };

  useEffect(() => {
    latest.current.mouseUp = handleMouseUp;
  });

  useEffect(() => {
    const up = () => latest.current.mouseUp();
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);

  const handleDoubleClick = (event: React.MouseEvent<HTMLTableElement>) => {
    const hit = cellFrom(event.target);
    if (hit?.kind !== "cell" || readOnly) return;
    select({ r: hit.r, c: hit.c });
    const index = visible[hit.r];
    setEdit({ value: rows[index]?.cells[hit.c] ?? "" });
    focusGrid();
  };

  const openMenu = (kind: MenuState["kind"], x: number, y: number) => {
    const width = 290;
    const height = kind === "cells" ? 470 : 330;
    setMenu({
      kind,
      x: Math.max(8, Math.min(x, window.innerWidth - width - 8)),
      y: Math.max(8, Math.min(y, window.innerHeight - height - 8)),
    });
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLTableElement>) => {
    const hit = cellFrom(event.target);
    if (!hit) return;
    event.preventDefault();
    openMenu(hit.kind === "col" ? "column" : "cells", event.clientX, event.clientY);
  };

  // fecha os menus ao clicar fora
  useEffect(() => {
    if (!menu) return;
    const close = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenu(null);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [menu]);

  const runMenu = (action: () => void) => () => {
    setMenu(null);
    action();
    focusGrid();
  };

  // ------------------------------------------------------------------ desenho

  const selectedRowsCount = range.r2 - range.r1 + 1;
  const rowsWord = selectedRowsCount === 1 ? "linha" : `${selectedRowsCount} linhas`;
  const activeNumber = activeIndex !== undefined ? activeIndex + 1 : 1;

  const matchLabelFor = (row: SheetRow) => {
    const id = results[row.id]?.matchRowId;
    if (!id) return "Linha da planilha";
    const position = rowPosition.get(id);
    return position === undefined ? "Linha removida da planilha" : `Linha ${position + 1} da planilha`;
  };

  const toolButton = "inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-foreground hover:bg-white disabled:pointer-events-none disabled:opacity-40 dark:hover:bg-zinc-900";

  const cellsMenu: MenuItem[] = [
    { label: "Recortar", shortcut: "Ctrl+X", action: () => cutRange(), hidden: readOnly },
    { label: "Copiar", shortcut: "Ctrl+C", action: () => copyRange() },
    { label: "Colar", shortcut: "Ctrl+V", action: () => pasteFromClipboard(), hidden: readOnly },
    { label: "Colar como novas linhas", action: () => pasteFromClipboard(range, true), hidden: readOnly },
    "sep",
    { label: "Limpar conteúdo", shortcut: "Delete", action: () => clearSelection(), hidden: readOnly },
    "sep",
    { label: `Inserir ${rowsWord} acima`, action: () => insert("above"), hidden: readOnly },
    { label: `Inserir ${rowsWord} abaixo`, shortcut: "Ctrl+Shift+=", action: () => insert("below"), hidden: readOnly },
    { label: `Duplicar ${rowsWord}`, shortcut: "Ctrl+Shift+D", action: duplicate, hidden: readOnly },
    { label: "Mover para cima", shortcut: "Alt+↑", action: () => moveRows(-1), hidden: readOnly },
    { label: "Mover para baixo", shortcut: "Alt+↓", action: () => moveRows(1), hidden: readOnly },
    "sep",
    { label: "Ordenar dezenas das linhas", action: sortNumbers, hidden: readOnly || ordered },
    { label: "Selecionar linhas inteiras", shortcut: "Shift+Espaço", action: selectRows },
    "sep",
    { label: `Excluir ${rowsWord}`, shortcut: "Ctrl+-", action: removeRows, danger: true, hidden: readOnly },
  ];

  const columnTarget: CellRange = { r1: 0, r2: lastRow, c1: range.c1, c2: range.c2 };
  const columnMenu: MenuItem[] = [
    { label: "Selecionar coluna", shortcut: "Ctrl+Espaço", action: selectColumns },
    { label: "Copiar coluna", shortcut: "Ctrl+C", action: () => copyRange(columnTarget) },
    { label: "Recortar coluna", shortcut: "Ctrl+X", action: () => cutRange(columnTarget), hidden: readOnly },
    { label: "Colar na coluna", shortcut: "Ctrl+V", action: () => pasteFromClipboard(columnTarget), hidden: readOnly },
    { label: "Limpar coluna", shortcut: "Delete", action: () => clearSelection(columnTarget), hidden: readOnly },
    { label: "Mover para a esquerda", shortcut: "Alt+←", action: () => moveColumns(-1), hidden: readOnly },
    { label: "Mover para a direita", shortcut: "Alt+→", action: () => moveColumns(1), hidden: readOnly },
  ];

  const visibleMenuItems = (menu?.kind === "cells" ? cellsMenu : columnMenu).filter(
    (item, index, list) =>
      item !== "sep"
        ? !item.hidden
        : index > 0 && list.slice(0, index).some((prev) => prev !== "sep" && !prev.hidden) && list.slice(index + 1).some((nextItem) => nextItem !== "sep" && !nextItem.hidden)
  );

  return (
    <div className="flex flex-col gap-3">
      <div
        role="toolbar"
        aria-label="Ferramentas da planilha"
        className="flex flex-wrap items-center gap-1 rounded-lg border bg-muted/40 p-1.5"
      >
        {!readOnly && (
          <>
            <button type="button" className={toolButton} onClick={onUndo} disabled={!canUndo} title="Desfazer (Ctrl+Z)">
              <Undo2 className="h-4 w-4" />
              Desfazer
            </button>
            <button type="button" className={toolButton} onClick={onRedo} disabled={!canRedo} title="Refazer (Ctrl+Y)">
              <Redo2 className="h-4 w-4" />
              Refazer
            </button>
            <span aria-hidden="true" className="mx-1 h-6 w-px bg-border" />
            <button type="button" className={toolButton} onClick={() => { insert("below"); focusGrid(); }} disabled={structuralLocked} title="Inserir linha abaixo (Ctrl+Shift+=)">
              <Plus className="h-4 w-4" />
              Inserir linha
            </button>
            <button type="button" className={toolButton} onClick={() => { duplicate(); focusGrid(); }} disabled={structuralLocked} title="Duplicar linhas (Ctrl+Shift+D)">
              <Copy className="h-4 w-4" />
              Duplicar
            </button>
            <button type="button" className={toolButton} onClick={() => { moveRows(-1); focusGrid(); }} disabled={structuralLocked || range.r1 === 0} title="Mover linhas para cima (Alt+↑)" aria-label="Mover linhas para cima">
              <ArrowUp className="h-4 w-4" />
            </button>
            <button type="button" className={toolButton} onClick={() => { moveRows(1); focusGrid(); }} disabled={structuralLocked || range.r2 >= rows.length - 1} title="Mover linhas para baixo (Alt+↓)" aria-label="Mover linhas para baixo">
              <ArrowDown className="h-4 w-4" />
            </button>
            <button type="button" className={cn(toolButton, "text-red-600")} onClick={() => { removeRows(); focusGrid(); }} disabled={structuralLocked} title="Excluir linhas (Ctrl+-)">
              <Trash2 className="h-4 w-4" />
              Excluir linhas
            </button>
            <span aria-hidden="true" className="mx-1 h-6 w-px bg-border" />
            {!ordered && (
              <button type="button" className={toolButton} onClick={() => { sortNumbers(); focusGrid(); }} title="Ordenar as dezenas de cada linha selecionada">
                <ArrowDown01 className="h-4 w-4" />
                Ordenar dezenas
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={toolButton} disabled={structuralLocked} title="Remover linhas vazias ou repetidas">
                  <Eraser className="h-4 w-4" />
                  Limpar planilha
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" onCloseAutoFocus={(event) => { event.preventDefault(); focusGrid(); }}>
                <DropdownMenuItem onClick={removeEmpty}>Remover linhas vazias</DropdownMenuItem>
                <DropdownMenuItem onClick={removeDuplicates}>Remover jogos repetidos na planilha</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <span aria-hidden="true" className="mx-1 h-6 w-px bg-border" />
          </>
        )}
        <button type="button" className={toolButton} onClick={openFind} title="Localizar dezena (Ctrl+F)">
          <Search className="h-4 w-4" />
          Localizar
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={cn(toolButton, filter !== "all" && "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200")} title="Mostrar só algumas linhas">
              <Filter className="h-4 w-4" />
              Mostrar: {FILTER_LABELS[filter]}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" onCloseAutoFocus={(event) => { event.preventDefault(); focusGrid(); }}>
            <DropdownMenuLabel>Mostrar linhas</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {FILTERS.map((option) => (
              <DropdownMenuItem key={option} onClick={() => changeFilter(option)} className={cn(option === filter && "font-semibold")}>
                {FILTER_LABELS[option]} ({filterCounts[option]})
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <button type="button" className={toolButton} onClick={() => { nextProblem(); }} title="Ir para o próximo problema (F8)">
          Próximo problema
          <kbd className="rounded border bg-background px-1 font-sans text-[11px] font-normal text-muted-foreground">F8</kbd>
        </button>
        <button type="button" className={cn(toolButton, "ml-auto text-muted-foreground")} onClick={() => setShortcutsOpen(true)} title="Atalhos do teclado (?)">
          <Keyboard className="h-4 w-4" />
          Atalhos
        </button>
      </div>

      {findOpen && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/30">
          <label htmlFor="sheet-find" className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900 dark:text-amber-200">
            <Search className="h-4 w-4" />
            Localizar dezena
          </label>
          <input
            id="sheet-find"
            ref={findRef}
            value={findQuery}
            inputMode="numeric"
            autoComplete="off"
            onChange={(event) => {
              setFindQuery(event.target.value.replace(/\D/g, "").slice(0, 2));
              setFindIndex(-1);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === "F3") {
                event.preventDefault();
                stepFind(event.shiftKey ? -1 : 1);
              } else if (event.key === "Escape") {
                event.preventDefault();
                closeFind();
              }
            }}
            className="w-20 rounded-md border bg-background px-2 py-1 text-center text-sm"
          />
          <span className="text-sm text-muted-foreground">
            {findQuery === ""
              ? "Digite a dezena e tecle Enter"
              : matches.length === 0
                ? "Nenhuma ocorrência nas linhas visíveis"
                : `${currentMatch ? `Ocorrência ${findIndex + 1} de ` : ""}${matches.length} ${matches.length === 1 ? "ocorrência" : "ocorrências"}`}
          </span>
          <button type="button" className="rounded-md border bg-background p-1" onClick={() => stepFind(-1)} aria-label="Ocorrência anterior (Shift+F3)">
            <ArrowUp className="h-4 w-4" />
          </button>
          <button type="button" className="rounded-md border bg-background p-1" onClick={() => stepFind(1)} aria-label="Próxima ocorrência (F3)">
            <ArrowDown className="h-4 w-4" />
          </button>
          <button type="button" className="ml-auto rounded-md p-1 text-amber-900 dark:text-amber-200" onClick={closeFind} aria-label="Fechar a busca (Esc)">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {filter !== "all" && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200">
          <span>
            Mostrando {visible.length} de {rows.length} linhas ({FILTER_LABELS[filter].toLowerCase()}). As outras continuam na planilha.
          </span>
          <button type="button" className="font-medium underline" onClick={() => changeFilter("all")}>
            Mostrar todas
          </button>
        </div>
      )}

      <div className="flex items-stretch overflow-hidden rounded-lg border text-sm">
        <span className="flex items-center whitespace-nowrap border-r bg-muted/40 px-3 font-semibold text-muted-foreground">
          L{activeNumber} · {colLabel(anchor.c)}
        </span>
        <span className="flex min-w-0 flex-1 items-center px-3 font-medium">{edit ? edit.value : activeValue || "—"}</span>
        <span className="hidden items-center whitespace-nowrap border-l px-3 text-muted-foreground sm:flex">
          Linha {activeNumber}, {ordered ? `coluna ${anchor.c + 1}` : `dezena ${pad2(anchor.c + 1)}`}
        </span>
      </div>

      <div className="max-h-[70vh] overflow-auto rounded-lg border">
        <div ref={wrapperRef} className="relative min-w-full" style={{ width: "max-content" }}>
          <table
            className="border-collapse text-sm"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onDoubleClick={handleDoubleClick}
            onContextMenu={handleContextMenu}
            role="grid"
            aria-rowcount={visible.length + 1}
            aria-colcount={columns + 2}
          >
            <thead className="sticky top-0 z-20 bg-muted">
              <tr>
                <th scope="col" className="sticky left-0 z-30 w-14 min-w-14 border-b border-r bg-muted p-0">
                  <button
                    type="button"
                    className="h-9 w-full text-[11px] font-semibold text-muted-foreground"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={() => { selectAll(); focusGrid(); }}
                    title="Selecionar tudo (Ctrl+A)"
                  >
                    Linha
                  </button>
                </th>
                {Array.from({ length: columns }, (_, c) => {
                  const selectedColumn = fullColumns && c >= range.c1 && c <= range.c2;
                  const inCols = c >= range.c1 && c <= range.c2;
                  return (
                    <th
                      key={c}
                      scope="col"
                      data-col-header={c}
                      className={cn(
                        "group h-9 min-w-9 cursor-pointer select-none border-b border-r px-0.5 text-center text-xs font-semibold",
                        selectedColumn
                          ? "bg-blue-600 text-white"
                          : inCols
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200"
                            : "text-foreground"
                      )}
                    >
                      <span className="inline-flex items-center">
                        {colLabel(c)}
                        <button
                          type="button"
                          data-col-menu={c}
                          className={cn("ml-0.5 rounded opacity-0 group-hover:opacity-100 focus:opacity-100", selectedColumn && "opacity-100")}
                          aria-label={`Ações da coluna ${colLabel(c)}`}
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            if (!(fullColumns && c >= range.c1 && c <= range.c2)) select({ r: 0, c }, { r: lastRow, c }, false);
                            const box = event.currentTarget.getBoundingClientRect();
                            openMenu("column", box.left - 8, box.bottom + 4);
                            focusGrid();
                          }}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </span>
                    </th>
                  );
                })}
                <th scope="col" className="min-w-52 border-b px-2 text-left text-xs font-semibold">
                  Maior coincidência
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((index, r) => {
                const row = rows[index];
                const inRows = r >= range.r1 && r <= range.r2;
                const cols = findByRow.get(r);
                return (
                  <GridRow
                    key={row.id}
                    row={row}
                    r={r}
                    number={index + 1}
                    rules={rules}
                    zebra={r % 2 === 1}
                    selC1={inRows ? range.c1 : -1}
                    selC2={inRows ? range.c2 : -1}
                    selTop={r === range.r1}
                    selBottom={r === range.r2}
                    activeCol={r === anchor.r ? anchor.c : -1}
                    fullRow={inRows && fullRows}
                    findCols={cols ? cols.join(",") : ""}
                    currentFindCol={currentMatch && currentMatch.r === r ? currentMatch.c : -1}
                    result={results[row.id]}
                    matchLabel={matchLabelFor(row)}
                    dimmed={!!moving && r >= moving.r1 && r <= moving.r2}
                    dropTop={moveTarget === r}
                    dropBottom={moveTarget === visible.length && r === visible.length - 1}
                    readOnly={readOnly}
                  />
                );
              })}
            </tbody>
          </table>

          <input
            ref={editorRef}
            aria-label={`Linha ${activeNumber}, ${ordered ? `coluna ${anchor.c + 1}` : `dezena ${pad2(anchor.c + 1)}`}`}
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            readOnly={readOnly}
            value={edit ? edit.value : ""}
            onChange={handleEditorChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={handleEditorBlur}
            className={cn(
              "absolute z-10 border-0 p-0 text-center text-sm font-medium outline-none",
              edit
                ? "pointer-events-auto bg-white text-foreground ring-2 ring-inset ring-blue-600 dark:bg-zinc-900"
                : "pointer-events-none bg-transparent text-transparent caret-transparent"
            )}
            style={editorBox}
          />
        </div>
      </div>

      <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 border-t pt-2 text-xs text-muted-foreground">
        <span>
          <strong className="font-semibold text-blue-700 dark:text-blue-300">Seleção:</strong> {describe(range)} ({rangeSize(range).cells}{" "}
          {rangeSize(range).cells === 1 ? "célula" : "células"})
          {notice && <span className="ml-2 text-foreground">· {notice}</span>}
        </span>
        <span>
          {rows.length} {rows.length === 1 ? "linha" : "linhas"} · {filled} {filled === 1 ? "jogo preenchido" : "jogos preenchidos"} ·{" "}
          {incomplete} {incomplete === 1 ? "incompleto" : "incompletos"}
          {!readOnly && ` · ${undoSteps} ${undoSteps === 1 ? "passo" : "passos"} para desfazer`}
        </span>
      </div>

      {menu && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={menu.kind === "cells" ? "Ações da seleção" : "Ações da coluna"}
          className="fixed z-50 w-[290px] rounded-lg border bg-popover p-1.5 text-sm shadow-xl"
          style={{ left: menu.x, top: menu.y }}
          onMouseDown={(event) => event.preventDefault()}
        >
          {visibleMenuItems.map((item, index) =>
            item === "sep" ? (
              <div key={`sep-${index}`} role="separator" className="mx-1.5 my-1 h-px bg-border" />
            ) : (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                onClick={runMenu(item.action)}
                className={cn(
                  "flex w-full items-center justify-between gap-4 rounded-md px-2.5 py-1.5 text-left hover:bg-muted",
                  item.danger ? "text-red-600" : "text-foreground"
                )}
              >
                <span>{item.label}</span>
                {item.shortcut && <span className="whitespace-nowrap text-xs text-muted-foreground">{item.shortcut}</span>}
              </button>
            )
          )}
          {menu.kind === "column" && !ordered && (
            <p className="mx-1.5 mb-0.5 mt-1 rounded-md bg-muted/60 px-2.5 py-2 text-xs text-muted-foreground">
              Nesta loteria a ordem das dezenas não muda o jogo. No Super Sete, cada coluna é uma posição.
            </p>
          )}
        </div>
      )}

      <Dialog open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
        <AppDialogContent
          icon={Keyboard}
          title="Atalhos da planilha"
          description="Funcionam com a planilha em foco. No Mac, use ⌘ no lugar de Ctrl."
          className="sm:max-w-4xl"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            focusGrid();
          }}
        >
          <AppDialogBody>
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
              {SHORTCUTS.map((group) => (
                <section key={group.title} className="space-y-1">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-blue-700 dark:text-blue-300">{group.title}</h3>
                  {group.items.map(([label, keys]) => (
                    <div key={label} className="flex items-center justify-between gap-3 border-b border-border/60 py-1 text-sm">
                      <span>{label}</span>
                      <kbd className="whitespace-nowrap rounded-md border border-b-2 bg-muted px-1.5 py-0.5 font-sans text-xs font-semibold">{keys}</kbd>
                    </div>
                  ))}
                </section>
              ))}
            </div>
          </AppDialogBody>
          <AppDialogFooter>
            <Button className={appDialogPrimaryButton} onClick={() => setShortcutsOpen(false)}>
              Entendi
            </Button>
          </AppDialogFooter>
        </AppDialogContent>
      </Dialog>
    </div>
  );
};
